import { Group } from './parseGroups';
import { ClusterRule, FlagInfo } from './types';

export interface Snapshot {
  enabled?: boolean;
  stringValue?: string | null;
  jsonValue?: unknown;
  audienceMode?: 'all' | 'groups';
  audienceGroups?: Group[];
}

export interface Change {
  setting: string;
  before: string;
  after: string;
}

type FlagShape = Pick<FlagInfo, 'supportsAudience' | 'valueOnly' | 'hasClusterRules' | 'stringParamLabel'>;

const ENV_DEFAULT = 'Environment default';

const onOff = (v: boolean | undefined) => (v ? 'On' : 'Off');

const audienceText = (s: Snapshot): string => {
  if (s.audienceMode !== 'groups') return 'All users';
  const names = (s.audienceGroups ?? []).map(g => g.name);
  return names.length > 0 ? `AD groups: ${names.join(', ')}` : 'AD groups: none selected';
};

const ruleLine = (r: ClusterRule): string => {
  const who: string[] = [];
  if (r.groups?.length) who.push(`${r.groups.length} group${r.groups.length === 1 ? '' : 's'} (${r.groups.map(g => g.name).join(', ')})`);
  if (r.groupPattern?.trim()) who.push(`pattern ${r.groupPattern.trim()}`);
  return `${r.cluster}: ${r.enabled === false ? 'off' : 'on'}, ${who.length ? who.join(' + ') : 'no groups'}`;
};

const rulesText = (s: Snapshot): string => {
  const rules = Array.isArray(s.jsonValue) ? (s.jsonValue as ClusterRule[]) : [];
  return rules.length > 0 ? rules.map(ruleLine).join('\n') : 'No rules';
};

const valueText = (s: Snapshot): string => (s.stringValue?.trim() ? s.stringValue.trim() : 'Not set');

/**
 * Turns one audit event into readable rows. A first save (before = null) lists every setting that applies to the feature;
 * later events list only what actually changed; a reset (after = null) shows the return to the environment default.
 */
export function describeChanges(before: Snapshot | null | undefined, after: Snapshot | null | undefined, flag: FlagShape): Change[] {
  if (!before && !after) return [];
  if (!after) return [{ setting: 'Source', before: 'Saved in DB', after: ENV_DEFAULT }];

  const fields: Array<{ setting: string; text: (s: Snapshot) => string }> = [];
  if (!flag.valueOnly) fields.push({ setting: flag.hasClusterRules ? 'Restrictions' : 'Status', text: s => onOff(s.enabled) });
  if (flag.stringParamLabel) fields.push({ setting: flag.stringParamLabel, text: valueText });
  if (flag.supportsAudience) fields.push({ setting: 'Applies to', text: audienceText });
  if (flag.hasClusterRules) fields.push({ setting: 'Rules', text: rulesText });

  if (!before) {
    return [{ setting: 'Source', before: ENV_DEFAULT, after: 'Saved in DB' }, ...fields.map(f => ({ setting: f.setting, before: '—', after: f.text(after) }))];
  }
  return fields
    .map(f => ({ setting: f.setting, before: f.text(before), after: f.text(after) }))
    .filter(c => c.before !== c.after);
}
